// VENDORED from Medplum — packages/core/src/fhirpath/patch.ts
//
// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
//
// Source commit: medplum/medplum@7eb2f8836f13a652120011dba1ed616e12279d24 (tag v5.1.17)
// Upstream PR: https://github.com/medplum/medplum/pull/9377
//
// Vendored because `fhirpathPatchTypedValue` is not exported from
// `@medplum/core`'s public barrel and is tree-shaken out of the published
// bundle. Delete this folder when Medplum exposes FHIRPath patch at the
// transport layer — see ../DELETE-WHEN-MEDPLUM-SHIPS-TRANSPORT-FHIRPATH.md.
//
// Adaptations from upstream (logic is otherwise verbatim):
// - Imports rewired to the `@medplum/core` public barrel and `rfc6902`.
//   Upstream uses Medplum's internal rfc6902 fork (`../patch`), which is also
//   unexported.
// - Upstream's `Pointer` builder is replaced by a local RFC-6901
//   token-escaping helper.
// - Upstream's `add(..., { implicitArrayCreation: true })` is replicated
//   explicitly: adding to an array element that does not exist yet creates
//   the array in place (rfc6902 has no implicit-array option).
// - When the element definition is unavailable (schema not indexed),
//   `isArray` falls back to a runtime `Array.isArray` check on the existing
//   value so the applier stays schema-optional.
// - The element lookup uses the local choice-type-aware `resolveSchemaElement`
//   instead of `getElementDefinitionForPath`, which throws "Multiple matching
//   types in path" for any `[x]` element (e.g. adding `occurrencePeriod`,
//   `valueQuantity`, or a field inside one).

import {
  evalFhirPathTyped,
  isObject,
  pathToJSONPointer,
  type TypedValue,
} from '@medplum/core'
import { applyPatch, type Operation } from 'rfc6902'
import { resolveSchemaElement } from '#src/fhirpath-patch/parameters-codec.ts'
import type { JsonObject } from '#src/json.ts'

export type FhirPathPatch =
  | { type: 'add'; path: string; name: string; value: TypedValue }
  | { type: 'insert'; path: string; value: TypedValue; index: number }
  | { type: 'delete'; path: string }
  | { type: 'replace'; path: string; value: TypedValue }
  | { type: 'move'; path: string; source: number; destination: number }

type TypedValueWithPath = TypedValue & { path: string }

export function fhirpathPatchTypedValue(
  original: TypedValue,
  patch: FhirPathPatch[]
): void {
  for (const op of patch) {
    const results = evalFhirPathTyped(op.path, [original])
    switch (op.type) {
      case 'add': {
        const base = getSingleValue(results, op)
        if (!isObject(base?.value)) {
          throw new Error(
            `Failed to resolve base object for add operation at path '${op.path}'`
          )
        }
        assertPath(base, op)
        if (!op.name) {
          throw new Error(
            `No name present for add operation at path '${op.path}'`
          )
        }

        // Construct pointer to location at which to add the value
        const basePointer = pathToJSONPointer(base.path)
        const fieldPointer = `${basePointer}/${escapePointerToken(op.name)}`
        const element = resolveSchemaElement(`${base.path}.${op.name}`)?.element
        const existing = (base.value as JsonObject)[op.name]
        const isArrayElement = element
          ? element.isArray
          : Array.isArray(existing)

        if (isArrayElement) {
          if (Array.isArray(existing)) {
            // Append to end of array
            applyOrThrow(original.value, {
              op: 'add',
              path: `${fieldPointer}/-`,
              value: op.value.value,
            })
          } else {
            // Upstream relies on implicitArrayCreation; create the array here.
            applyOrThrow(original.value, {
              op: 'add',
              path: fieldPointer,
              value: [op.value.value],
            })
          }
        } else {
          applyOrThrow(original.value, {
            op: 'add',
            path: fieldPointer,
            value: op.value.value,
          })
        }
        break
      }
      case 'replace': {
        const target = getSingleValue(results, op)
        if (!target) {
          throw new Error(
            `Failed to resolve target value for replace operation at path '${op.path}'`
          )
        }
        assertPath(target, op)

        applyOrThrow(original.value, {
          op: 'replace',
          path: pathToJSONPointer(target.path),
          value: op.value.value,
        })
        break
      }
      case 'move': {
        const basePath = assertCollectionBasePath(results, op)
        const basePointer = pathToJSONPointer(basePath)

        applyOrThrow(original.value, {
          op: 'move',
          from: `${basePointer}/${op.source}`,
          path: `${basePointer}/${op.destination}`,
        })
        break
      }
      case 'insert': {
        const basePath = assertCollectionBasePath(results, op)
        // Construct pointer to array index
        const basePointer = pathToJSONPointer(basePath)

        applyOrThrow(original.value, {
          op: 'add',
          path: `${basePointer}/${op.index}`,
          value: op.value.value,
        })
        break
      }
      case 'delete': {
        const target = getSingleValue(results, op)
        if (target) {
          assertPath(target, op)
          applyOrThrow(original.value, {
            op: 'remove',
            path: pathToJSONPointer(target.path),
          })
        }
      }
    }
  }
}

function applyOrThrow(doc: JsonObject, operation: Operation): void {
  const [error] = applyPatch(doc, [operation])
  if (error) {
    throw new Error(
      `Failed to apply ${operation.op} operation at '${operation.path}': ${error.message}`
    )
  }
}

function escapePointerToken(token: string): string {
  return token.replaceAll('~', '~0').replaceAll('/', '~1')
}

function assertPath(
  value: TypedValue,
  op: FhirPathPatch
): asserts value is TypedValueWithPath {
  if (!('path' in value)) {
    throw new Error(`Cannot resolve pointer for value at path '${op.path}'`)
  }
}

function getSingleValue(
  collection: TypedValue[],
  op: FhirPathPatch
): TypedValue | undefined {
  if (collection.length > 1) {
    throw new Error(
      `Resolved multiple base values for ${op.type} operation at path '${op.path}'`
    )
  }
  return collection[0]
}

const terminalIndexRegex = /\[(?<index>\d+)\]$/
function getTerminalIndex(path: string): number {
  const index = terminalIndexRegex.exec(path)?.groups?.index
  return index ? Number.parseInt(index, 10) : -1
}

function assertCollectionBasePath(
  collection: TypedValue[],
  op: FhirPathPatch
): string {
  if (getTerminalIndex(op.path) !== -1) {
    // Path should specify the collection, not a specific element
    throw new Error(
      `Failed to resolve base collection for ${op.type} operation at path '${op.path}'`
    )
  }
  if (!collection.length) {
    // FHIR JSON does not permit empty arrays, so this could not occur naturally in a resource
    throw new Error(
      `Failed to resolve base for ${op.type} operation at path '${op.path}'`
    )
  }

  assertPath(collection[0], op)

  // Assert first element
  const path = collection[0].path
  if (getTerminalIndex(path) !== 0) {
    throw new Error(
      `Failed to resolve base collection for ${op.type} operation at path '${op.path}'`
    )
  }
  // First element sets the base path
  const basePath = path.slice(0, path.lastIndexOf('['))

  for (let i = 1; i < collection.length; i++) {
    // All elements should follow sequentially under the base path
    const item = collection[i]
    assertPath(item, op)

    if (!item.path.startsWith(basePath)) {
      throw new Error(
        `Cannot patch heterogeneous collection for ${op.type} operation at path '${op.path}'`
      )
    }
    if (getTerminalIndex(item.path) !== i) {
      throw new Error(
        `Cannot patch misordered collection for ${op.type} operation at path '${op.path}'`
      )
    }
  }

  // Validate that indices are valid with the collection at this path
  if ('index' in op && !isValidIndex(op.index, collection)) {
    throw new Error(`Index out of bounds for ${op.type} operation: ${op.index}`)
  }
  if ('source' in op && !isValidIndex(op.source, collection)) {
    throw new Error(
      `Source index out of bounds for ${op.type} operation: ${op.source}`
    )
  }
  if ('destination' in op && !isValidIndex(op.destination, collection)) {
    throw new Error(
      `Destination index out of bounds for ${op.type} operation: ${op.destination}`
    )
  }
  return basePath
}

function isValidIndex(index: number, collection: TypedValue[]): boolean {
  return index >= 0 && index <= collection.length
}
